import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzOBv0uBlIglllBCIh8AQHJZKTzRF7jrfVe5I7qr2w8Tc4oy9QnFRsVS0gb9EprnxMliw/exec';

function formatDate(dateStr) {
  if (!dateStr) return "";
  // Parse date parts manually to avoid UTC-vs-local timezone shift
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

// Return YYYY-MM-DD in local time (not UTC — avoids off-by-one in AEST/AEDT)
function localDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function initDateRange() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 27);
  return { from: localDateStr(from), to: localDateStr(today) };
}

// The period to push for verification. The app's active period is the cycle CONTAINING
// today, which is still open mid-cycle — you verify a period once it has closed, so if
// the active period's end is in the future, the one to push is the cycle before it.
// A period the user has explicitly selected (on RCTI / Import) is already closed and is
// followed as-is, which is what keeps this page and the RCTI on the same days.
function verifyPeriodEnd(activeISO) {
  const [y, m, d] = (activeISO || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  const end = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (end > today) end.setDate(end.getDate() - 28);
  return localDateStr(end);
}

// The 28 days ENDING on a cut-off period end (the RCTI's own period), rather than the
// 28 days ending today. A summary a driver verifies has to cover the same days as the
// RCTI he's paid on, so this page follows the RCTI period rather than a rolling window.
function periodDateRange(periodEndISO) {
  const [y, m, d] = (periodEndISO || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  const end = new Date(y, m - 1, d);
  const from = new Date(end);
  from.setDate(end.getDate() - 27);
  return { from: localDateStr(from), to: localDateStr(end) };
}

export default function EndOfShiftPage() {
  const { activePeriodISO } = useApp();
  const cutOffPeriodISO = verifyPeriodEnd(activePeriodISO);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [dateRange, setDateRange] = useState(initDateRange());
  const [rangeTouched, setRangeTouched] = useState(false);
  const [shiftData, setShiftData] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [sentStatus, setSentStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("push");
  const [pushLog, setPushLog] = useState([]);
  const [message, setMessage] = useState("");
  const [includes, setIncludes] = useState({
    parcels: true, express: true, depotBags: true, doe: true
  });
  const [previewDriver, setPreviewDriver] = useState(null);
  const [teamLeaders, setTeamLeaders] = useState([]);
const [tlSummaryData, setTlSummaryData] = useState({});
// Who each leader's total is actually made of. Kept beside the rows (not merged into them)
// because rows are the emailed figures, while this is the audit of the team behind them.
const [tlMeta, setTlMeta] = useState({});
const [loadingTL, setLoadingTL] = useState(false);
const [tlSending, setTlSending] = useState({});
const [tlSentStatus, setTlSentStatus] = useState({});

useEffect(() => { fetchDrivers(); fetchTeamLeaders(); loadPushLog(); }, []);

// Default to the cut-off period being verified (the same days the RCTI covers) once
// Controls loads. Seeded from a rolling 28 days only until then, and never overrides
// dates the user has picked themselves.
useEffect(() => {
  if (!cutOffPeriodISO || rangeTouched) return;
  const r = periodDateRange(cutOffPeriodISO);
  if (r) setDateRange(r);
}, [cutOffPeriodISO, rangeTouched]);

  async function fetchDrivers() {
    setLoadingDrivers(true);
    try {
      const res = await fetch("/.netlify/functions/eos-drivers");
      const data = await res.json();
      if (data.drivers) {
        setDrivers(data.drivers);
        const all = {};
        data.drivers.forEach(d => { all[d.login] = true; });
        setSelectedDrivers(all);
      }
    } catch (e) { console.error("Failed to load drivers", e); }
    setLoadingDrivers(false);
  }
async function fetchTeamLeaders() {
    try {
      const res = await fetch(APPS_SCRIPT_URL + '?action=getTeamLeaders');
      const data = await res.json();
      console.log('Team leaders response:', JSON.stringify(data));
      if (data.teamLeaders) setTeamLeaders(data.teamLeaders);
    } catch (e) { console.error('Failed to load team leaders', e); }
  }

async function fetchTLSummary(login) {
    setLoadingTL(true);
    try {
      // Live Supabase read (parcel_events/doe/depots/drivers/network_drivers). The old
      // Apps Script getTeamLeaderSummaryData read the frozen Depots sheet (mirror dropped
      // 2026-07-02) → team-leader depot bags showed 0 from that date. Pass the TL's route
      // so the route-primary depot-bag rollup matches the old logic.
      const tlRoute = teamLeaders.find(t => t.login === login)?.route || '';
      const url = '/.netlify/functions/eos-tl-summary-sb'
        + '?login=' + encodeURIComponent(login)
        + '&from=' + dateRange.from
        + '&to=' + dateRange.to
        + '&route=' + encodeURIComponent(tlRoute);
      const res = await fetch(url);
      const data = await res.json();
      const tl = teamLeaders.find(t => t.login === login);
      const excludeSet = new Set(
        (tl?.excludeDates || []).map(d => {
          // Convert DD-Mon-YYYY to YYYY-MM-DD
          const monthMap = {Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12'};
          const parts = d.split('-');
          if (parts.length !== 3) return '';
          return `${parts[2]}-${monthMap[parts[1]] || '00'}-${parts[0].padStart(2,'0')}`;
        }).filter(Boolean)
      );
      const rows = (data.rows || []).filter(r => !excludeSet.has(r.date));
      setTlSummaryData(prev => ({ ...prev, [login]: rows }));
      setTlMeta(prev => ({ ...prev, [login]: { counted: data.counted || [], homeless: data.homeless || [], primary: !!data.primary } }));
    } catch (e) { console.error('Failed to load TL summary', e); }
    setLoadingTL(false);
  }

  async function sendTLSummary(tl) {
    setTlSending(prev => ({ ...prev, [tl.login]: true }));
    try {
      const rows = tlSummaryData[tl.login] || [];
      const u = tl.units || { parcels: true, express: true, depotBags: true };
      const res = await fetch('/.netlify/functions/eos-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driverLogin: tl.login,
          driverEmail: tl.email,
          driverName: tl.name,
          from: dateRange.from,
          to: dateRange.to,
          rows: rows.map(r => ({ date: r.date, parcels: r.parcels, express: r.express || 0, depotBags: r.depotBags, doe: 0 })),
          includes: { parcels: !!u.parcels, express: !!u.express, depotBags: !!u.depotBags, doe: false },
          message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTlSentStatus(prev => ({ ...prev, [tl.login]: 'sent' }));
      }
    } catch (e) { console.error('Failed to send TL summary', e); }
    setTlSending(prev => ({ ...prev, [tl.login]: false }));
  }

  async function fetchShiftDataForDriver(driver) {
    try {
      const url = "/.netlify/functions/eos-data-sb"
        + "?driverLogin=" + encodeURIComponent(driver.login)
        + "&from=" + dateRange.from
        + "&to=" + dateRange.to;
      const res = await fetch(url);
      const data = await res.json();
      return data.rows || [];
    } catch (e) { return []; }
  }

  useEffect(() => {
    if (previewDriver) {
      setLoadingData(true);
      fetchShiftDataForDriver(previewDriver).then(rows => {
        setShiftData(prev => ({ ...prev, [previewDriver.login]: rows }));
        setLoadingData(false);
      });
    }
  }, [previewDriver, dateRange]);

  function setRange(type) {
    setRangeTouched(true);
    if (type === "period") {
      const r = periodDateRange(cutOffPeriodISO);
      if (r) setDateRange(r);
      return;
    }
    const today = new Date();
    const fmt = localDateStr;
    let from = new Date(today);
    if (type === "week") {
      const day = today.getDay() || 7;
      from.setDate(today.getDate() - day + 1);
    } else if (type === "fortnight") {
      from.setDate(today.getDate() - 13);
    }
    setDateRange({ from: fmt(from), to: fmt(today) });
  }

  function toggleDriver(login) {
    setSelectedDrivers(prev => ({ ...prev, [login]: !prev[login] }));
  }

  function selectAll() {
    const all = {};
    drivers.forEach(d => { all[d.login] = true; });
    setSelectedDrivers(all);
  }

  function deselectAll() {
    const none = {};
    drivers.forEach(d => { none[d.login] = false; });
    setSelectedDrivers(none);
  }

  function loadPushLog() {
    try {
      const saved = localStorage.getItem("eos_push_log");
      if (saved) setPushLog(JSON.parse(saved));
    } catch(e) {}
  }

  function savePushLog(newLog) {
    localStorage.setItem("eos_push_log", JSON.stringify(newLog));
    setPushLog(newLog);
  }

  async function pushToDriver(driver) {
    const rows = await fetchShiftDataForDriver(driver);
    if (rows.length === 0) return { skipped: true };
    const res = await fetch("/.netlify/functions/eos-send", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        driverLogin: driver.login,
        driverEmail: driver.email,
        driverName: driver.name,
        from: dateRange.from,
        to: dateRange.to,
        rows,
        includes,
        message,
      }),
    });
    const data = await res.json();
    return data;
  }

  async function handlePushSelected() {
    const selected = drivers.filter(d => selectedDrivers[d.login] && d.email);
    if (selected.length === 0) return;
    setSendingAll(true);
    setSentStatus(null);
    const newLog = [];
    for (const driver of selected) {
      try {
        const result = await pushToDriver(driver);
        if (result.success) {
          newLog.push({
            driver: driver.login,
            name: driver.name,
            date: new Date().toLocaleDateString("en-AU"),
            from: dateRange.from,
            to: dateRange.to,
          });
        }
      } catch (e) { console.error("Failed for", driver.login, e); }
    }
    savePushLog([...newLog, ...pushLog].slice(0, 50));
    setSendingAll(false);
    setSentStatus("success");
  }

  const selectedCount = drivers.filter(d => selectedDrivers[d.login]).length;
  const eligibleCount = drivers.filter(d => selectedDrivers[d.login] && d.email).length;

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto", padding: "1.5rem 1rem", fontFamily: "var(--font-sans, sans-serif)" }}>

      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 500, margin: "0 0 4px 0" }}>Cut off period</h1>
        <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)", margin: 0 }}>
          Pull cut off period data from Google Sheets and push a PDF summary to your drivers via email.
        </p>
      </div>

      <div style={{ display: "flex", gap: "8px", marginBottom: "1.25rem" }}>
{[["push","Push to drivers"],["preview","Data preview"],["teamleaders","Team leaders"],["log","Push log"]].map(([id, label]) => (          <button key={id} onClick={() => setActiveTab(id)} style={{
            fontSize: "13px", padding: "6px 14px",
            borderRadius: "8px",
            border: activeTab === id ? "1px solid var(--hp-border, #ccc)" : "1px solid transparent",
            background: activeTab === id ? "var(--hp-surface, #f5f5f5)" : "transparent",
            color: activeTab === id ? "var(--hp-text, #111)" : "var(--hp-dim, #888)",
            fontWeight: activeTab === id ? 500 : 400,
            cursor: "pointer",
          }}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "push" && (
        <div>
          <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>Date range</div>
          <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            <div className="stack-mobile" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "var(--hp-dim, #888)", marginBottom: "5px" }}>From</div>
                <input type="date" value={dateRange.from} onChange={(e) => { setRangeTouched(true); setDateRange((r) => ({ ...r, from: e.target.value })); }} style={{ width: "100%", fontSize: "13px" }} />
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "var(--hp-dim, #888)", marginBottom: "5px" }}>To</div>
                <input type="date" value={dateRange.to} onChange={(e) => { setRangeTouched(true); setDateRange((r) => ({ ...r, to: e.target.value })); }} style={{ width: "100%", fontSize: "13px" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button onClick={() => setRange("period")}
                style={{ fontSize: "11px", padding: "5px 12px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "#1a1a1a", color: "#fff", cursor: "pointer", fontWeight: 500 }}>
                Current cut-off period
              </button>
              <button onClick={() => setRange("week")}
                style={{ fontSize: "11px", padding: "5px 12px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-dim, #888)", cursor: "pointer" }}>
                This week
              </button>
              <button onClick={() => setRange("fortnight")}
                style={{ fontSize: "11px", padding: "5px 12px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-dim, #888)", cursor: "pointer" }}>
                Last 2 weeks
              </button>
            </div>
          </div>

          <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>Data to include</div>
          <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            {[["parcels","Parcels"],["express","Express"],["depotBags","Depot bags"],["doe","DoE"]].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", marginBottom: "8px", cursor: "pointer" }}>
                <input type="checkbox" checked={includes[key]} onChange={(e) => setIncludes((i) => ({ ...i, [key]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Select drivers — {selectedCount} selected, {eligibleCount} with email
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={selectAll} style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-dim, #888)", cursor: "pointer" }}>Select all</button>
              <button onClick={deselectAll} style={{ fontSize: "11px", padding: "4px 10px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-dim, #888)", cursor: "pointer" }}>Deselect all</button>
            </div>
          </div>

          <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "0.5rem 1rem", marginBottom: "1rem", maxHeight: "320px", overflowY: "auto" }}>
            {loadingDrivers ? (
              <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)", padding: "8px 0" }}>Loading drivers...</p>
            ) : drivers.map((drv) => (
              <div key={drv.login} onClick={() => toggleDriver(drv.login)}
                style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderBottom: "1px solid var(--hp-border, #eee)", cursor: "pointer", opacity: selectedDrivers[drv.login] ? 1 : 0.4 }}>
                <div style={{
                  width: "34px", height: "34px", borderRadius: "50%", flexShrink: 0,
                  background: selectedDrivers[drv.login] ? "#dbeafe" : "var(--hp-bg, #eee)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11px", fontWeight: 500,
                  color: selectedDrivers[drv.login] ? "#1d4ed8" : "var(--hp-dim, #888)",
                }}>
                  {drv.login.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 500 }}>{drv.login}</div>
                  <div style={{ fontSize: "11px", color: "var(--hp-dim, #888)" }}>{drv.email || "No email on file"}</div>
                </div>
                <input type="checkbox" readOnly checked={!!selectedDrivers[drv.login]} style={{ width: "14px", height: "14px" }} />
              </div>
            ))}
          </div>

          <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>Optional message</div>
          <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Please review and contact me if anything looks off."
              style={{ width: "100%", fontSize: "13px", padding: "8px 10px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-text, #111)", resize: "vertical", minHeight: "64px", boxSizing: "border-box" }} />
          </div>

          {sentStatus === "success" && (
            <div style={{ background: "#dcfce7", color: "#166534", borderRadius: "8px", padding: "10px 14px", fontSize: "13px", marginBottom: "1rem" }}>
              Cut off period summaries sent successfully.
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <button onClick={() => { setActiveTab("preview"); setPreviewDriver(drivers.find(d => selectedDrivers[d.login])); }}
              style={{ fontSize: "13px", padding: "7px 16px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", cursor: "pointer" }}>
              Preview first driver
            </button>
            <button onClick={handlePushSelected} disabled={eligibleCount === 0 || sendingAll}
              style={{ fontSize: "13px", padding: "7px 16px", borderRadius: "8px", border: "none", background: "#111", color: "#fff", cursor: "pointer", opacity: (eligibleCount === 0 || sendingAll) ? 0.5 : 1 }}>
              {sendingAll ? "Sending..." : `Push to ${eligibleCount} driver${eligibleCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}

      {activeTab === "preview" && (
        <div>
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>Preview driver</div>
            <select value={previewDriver?.login || ""} onChange={e => setPreviewDriver(drivers.find(d => d.login === e.target.value))}
              style={{ fontSize: "13px", padding: "7px 10px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", color: "var(--hp-text, #111)", width: "100%" }}>
              <option value="">Select a driver...</option>
              {drivers.filter(d => selectedDrivers[d.login]).map(d => (
                <option key={d.login} value={d.login}>{d.login}</option>
              ))}
            </select>
          </div>

          {loadingData ? (
            <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>Loading data...</p>
          ) : previewDriver && shiftData[previewDriver.login] ? (
            shiftData[previewDriver.login].length === 0 ? (
              <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>No data found for this driver in the selected range.</p>
            ) : (
              <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "1rem 1.25rem" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" }}>Date</th>
                        {includes.parcels && <th style={{ textAlign: "right", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" }}>Parcels</th>}
                        {includes.express && <th style={{ textAlign: "right", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" }}>Express</th>}
                        {includes.depotBags && <th style={{ textAlign: "right", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" }}>Depot bags</th>}
                        {includes.doe && <th style={{ textAlign: "right", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" }}>DoE</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {shiftData[previewDriver.login].map((row, i) => (
                        <tr key={i}>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", fontSize: "12px" }}>{formatDate(row.date)}</td>
                          {includes.parcels && <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", textAlign: "right" }}>{row.parcels || 0}</td>}
                          {includes.express && <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", textAlign: "right" }}>{row.express || 0}</td>}
                          {includes.depotBags && <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", textAlign: "right" }}>{row.depotBags || 0}</td>}
                          {includes.doe && <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", textAlign: "right" }}>{row.doe || 0}</td>}
                        </tr>
                      ))}
                      <tr style={{ background: "var(--hp-bg, #eee)" }}>
                        <td style={{ padding: "7px 8px", fontWeight: 500, fontSize: "12px", borderTop: "2px solid var(--hp-border, #ccc)" }}>Total</td>
                        {includes.parcels && <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 500, borderTop: "2px solid var(--hp-border, #ccc)" }}>{shiftData[previewDriver.login].reduce((s,r) => s + (r.parcels||0), 0).toLocaleString()}</td>}
                        {includes.express && <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 500, borderTop: "2px solid var(--hp-border, #ccc)" }}>{shiftData[previewDriver.login].reduce((s,r) => s + (r.express||0), 0).toLocaleString()}</td>}
                        {includes.depotBags && <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 500, borderTop: "2px solid var(--hp-border, #ccc)" }}>{shiftData[previewDriver.login].reduce((s,r) => s + (r.depotBags||0), 0).toLocaleString()}</td>}
                        {includes.doe && <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 500, borderTop: "2px solid var(--hp-border, #ccc)" }}>{shiftData[previewDriver.login].reduce((s,r) => s + (r.doe||0), 0).toLocaleString()}</td>}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : (
            <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>Select a driver above to preview their data.</p>
          )}
        </div>
      )}
      {activeTab === "teamleaders" && (
  <div>
    <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>
      Team leader summaries
    </div>
    {/* This tab summarises whatever range the Date range panel holds, so show it — a leader
        verifies these figures against his RCTI, which covers the cut-off period. */}
    <div style={{ fontSize: "12px", color: "var(--hp-dim, #888)", marginBottom: "12px" }}>
      {formatDate(dateRange.from)} to {formatDate(dateRange.to)}
      {cutOffPeriodISO && dateRange.to === cutOffPeriodISO
        ? " · matches the RCTI cut-off period"
        : " · not the cut-off period — set the date range on Push to drivers"}
    </div>
    {(() => {
      const ALL_COLS = [["parcels","Parcels"],["express","Express"],["depotBags","Depot Bags"]];
      // Only TLs who actually report a parcel/express/depot unit appear on this page.
      const shown = teamLeaders.filter(tl => { const u = tl.units || {}; return u.parcels || u.express || u.depotBags; });
      if (shown.length === 0) return <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>No team leaders found.</p>;
      const thBase = { textAlign: "right", padding: "7px 8px", background: "var(--hp-bg, #eee)", fontWeight: 500, fontSize: "11px", borderBottom: "1px solid var(--hp-border, #eee)" };
      return shown.map(tl => {
        const u = tl.units || {};
        const cols = ALL_COLS.filter(([k]) => u[k]);
        return (
          <div key={tl.login} style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600 }}>{tl.name || tl.login}</div>
                <div style={{ fontSize: "11px", color: "var(--hp-dim, #888)" }}>{tl.login} · {tl.route} · {tl.email || "No email"}</div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => fetchTLSummary(tl.login)}
                  style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--hp-border, #ccc)", background: "transparent", cursor: "pointer" }}>
                  Load data
                </button>
                {tlSummaryData[tl.login] && (
                  <button onClick={() => sendTLSummary(tl)} disabled={tlSending[tl.login]}
                    style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "8px", border: "none", background: "#111", color: "#fff", cursor: "pointer", opacity: tlSending[tl.login] ? 0.5 : 1 }}>
                    {tlSending[tl.login] ? "Sending..." : tlSentStatus[tl.login] === "sent" ? "✓ Sent" : "Send summary"}
                  </button>
                )}
              </div>
            </div>
            {/* The drivers behind the total. A leader's summary is his only check on the RCTI,
                and a driver dropping out of the team is invisible in a date/total table — that
                is how DONGB3 verified 48,924 against an RCTI of 52,302. Count these against the
                Parcel Audit's driver list for the same route before sending. */}
            {tlMeta[tl.login] && tlMeta[tl.login].counted.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <details>
                  <summary style={{ fontSize: "12px", color: "var(--hp-dim, #888)", cursor: "pointer" }}>
                    {tlMeta[tl.login].counted.length} driver{tlMeta[tl.login].counted.length !== 1 ? "s" : ""} counted
                    {tlMeta[tl.login].primary ? ` — every driver on ${tl.route}` : ""} · check against Parcel Audit
                  </summary>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
                    {tlMeta[tl.login].counted.map(c => (
                      <span key={c.login} style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "6px", background: "var(--hp-bg, #eee)", color: "var(--hp-dim, #888)" }}>
                        {c.login} · {c.parcels.toLocaleString()}
                      </span>
                    ))}
                  </div>
                </details>
              </div>
            )}
            {tlMeta[tl.login] && tlMeta[tl.login].homeless.length > 0 && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: "8px", padding: "9px 12px", fontSize: "12px", marginBottom: "12px" }}>
                <strong>Parcels counted for nobody.</strong> These drivers delivered in this period but have no route,
                so their parcels reach no leader's summary — fix their Drivers row before sending:{" "}
                {tlMeta[tl.login].homeless.map(h => `${h.login} (${h.parcels.toLocaleString()} — ${h.reason})`).join(", ")}
              </div>
            )}
            {loadingTL && !tlSummaryData[tl.login] ? (
              <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>Loading...</p>
            ) : tlSummaryData[tl.login] ? (
              tlSummaryData[tl.login].length === 0 ? (
                <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>No data found for this period.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr>
                        <th style={{ ...thBase, textAlign: "left" }}>Date</th>
                        {cols.map(([k, label]) => <th key={k} style={thBase}>{label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {tlSummaryData[tl.login].map((row, i) => (
                        <tr key={i}>
                          <td style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", fontSize: "12px" }}>{formatDate(row.date)}</td>
                          {cols.map(([k]) => <td key={k} style={{ padding: "7px 8px", borderBottom: "1px solid var(--hp-border, #eee)", textAlign: "right" }}>{(row[k] || 0).toLocaleString()}</td>)}
                        </tr>
                      ))}
                      <tr style={{ background: "var(--hp-bg, #eee)" }}>
                        <td style={{ padding: "7px 8px", fontWeight: 500, fontSize: "12px", borderTop: "2px solid var(--hp-border, #ccc)" }}>Total</td>
                        {cols.map(([k]) => <td key={k} style={{ padding: "7px 8px", textAlign: "right", fontWeight: 500, borderTop: "2px solid var(--hp-border, #ccc)" }}>{tlSummaryData[tl.login].reduce((s, r) => s + (r[k] || 0), 0).toLocaleString()}</td>)}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </div>
        );
      });
    })()}
  </div>
)}

      {activeTab === "log" && (
        <div>
          <div style={{ fontSize: "11px", fontWeight: 500, color: "var(--hp-dim, #888)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>Recent pushes</div>
          {pushLog.length === 0 ? (
            <p style={{ fontSize: "13px", color: "var(--hp-dim, #888)" }}>No pushes yet.</p>
          ) : (
            <div style={{ background: "var(--hp-surface, #f9f9f9)", border: "1px solid var(--hp-border, #eee)", borderRadius: "12px", padding: "0.5rem 1rem" }}>
              {pushLog.map((entry, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 0", borderBottom: i < pushLog.length - 1 ? "1px solid var(--hp-border, #eee)" : "none" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 500, color: "#166534", flexShrink: 0 }}>
                    {entry.driver.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 500 }}>{entry.name || entry.driver}</div>
                    <div style={{ fontSize: "11px", color: "var(--hp-dim, #888)" }}>{entry.date} · {formatDate(entry.from)} – {formatDate(entry.to)}</div>
                  </div>
                  <span style={{ fontSize: "11px", padding: "3px 9px", borderRadius: "8px", background: "#dcfce7", color: "#166534", fontWeight: 500 }}>Sent</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
